<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import CInput from '@/components/shared/CInput.vue';
import type { ImagePlaceholderElement, WordSearchConfig } from '@/typings/customilyElements/canvasElements';

const props = defineProps<{
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
}>();

const model = reactive({
    gridCols: 20,
    gridRows: 20
});

const clampValue = (value: number, min = 5, max = 50): number => {
    return Math.max(min, Math.min(max, value));
};

watch(
    () => props.imageElement,
    (element) => {
        if (!element || !element.wordSearchConfig) return;

        const config = element.wordSearchConfig;
        model.gridCols = clampValue(config.gridCols ?? 20);
        model.gridRows = clampValue(config.gridRows ?? 20);
    },
    { immediate: true }
);

const disabled = computed(() => !!props.imageElement?.locked);

const updateGridCols = (value = 0) => {
    const clampedValue = clampValue(value);
    model.gridCols = clampedValue;
    updateWordSearchConfig({ gridCols: clampedValue });
};

const updateGridRows = (value = 0) => {
    const clampedValue = clampValue(value);
    model.gridRows = clampedValue;
    updateWordSearchConfig({ gridRows: clampedValue });
};

const updateWordSearchConfig = (config: Partial<WordSearchConfig>) => {
    const element = props.imageElement;
    if (!element) return;

    const newConfig = { ...element.wordSearchConfig, ...config } as WordSearchConfig;
    element.set('wordSearchConfig', newConfig);
    if (props.editMode) element.setEditedProp('wordSearchConfig');
};
</script>

<template>
    <div class="word-search-grid-wrapper">
        <div class="row">
            <div class="col-6 d-flex align-items-center">
                <c-input
                    v-model="model.gridCols"
                    type="number"
                    class="flex-fill"
                    label="Grid Cols"
                    :disabled="disabled"
                    @blur="updateGridCols(model.gridCols)"
                />
            </div>
            <div class="col-6 d-flex align-items-center">
                <c-input
                    v-model="model.gridRows"
                    type="number"
                    class="flex-fill"
                    label="Grid Rows"
                    :disabled="disabled"
                    @blur="updateGridRows(model.gridRows)"
                />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.word-search-grid-wrapper {
    .row {
        row-gap: 0.625rem;
    }
}
</style>
